/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import FilterIcon from '@atlaskit/icon/core/filter';
import IssuesIcon from '@atlaskit/icon/core/work-items';
import LabelIcon from '@atlaskit/icon/core/tag';
import TeamsIcon from '@atlaskit/icon/core/teams';
import ProjectIcon from '@atlaskit/icon/core/project';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { type IntlShape, defineMessage } from '@atlassian/jira-intl';
import { BOARD_SOURCE_FIELDS } from './boardTypes.constants.tsx';
import type { SourceTypeOptionGroup } from './BoardCreateFormValues.types.tsx';

const teamTitle = defineMessage({
	id: 'software-board-create.modal.form.source-type-picker.team-title',
	defaultMessage: 'Team',
	description: 'Label for the team source type dropdown option',
});

const teamSelectedTitle = defineMessage({
	id: 'software-board-create.modal.form.source-type-picker.team-selected-title',
	defaultMessage: 'Issues based on <strong>Team</strong>',
	description: 'Label for when team is selected as the source type of the new board',
});

const teamDescription = defineMessage({
	id: 'software-board-create.modal.form.source-type-picker.team-description',
	defaultMessage: 'Show all issues assigned to a team',
	description: 'Description for the team source type dropdown option',
});

const issueTypesTitle = defineMessage({
	id: 'software-board-create.modal.form.source-type-picker.issue-types-title',
	defaultMessage: 'Issue type',
	description: 'Label for the issue types source type option',
});

const issueTypesSelectedTitle = defineMessage({
	id: 'software-board-create.modal.form.source-type-picker.issue-types-selected-title',
	defaultMessage: 'Issues based on <strong>Issue type</strong>',
	description: 'Label for when issue type is selected as the source type of the new board',
});

const issueTypesDescription = defineMessage({
	id: 'software-board-create.modal.form.source-type-picker.issue-types-description',
	defaultMessage: 'Show all issues associated with an issue type',
	description: 'Description for the issue types source type option',
});

const labelsTitle = defineMessage({
	id: 'software-board-create.modal.form.source-type-picker.labels-title',
	defaultMessage: 'Label',
	description: 'Label for the labels source type option',
});

const labelsSelectedTitle = defineMessage({
	id: 'software-board-create.modal.form.source-type-picker.labels-selected-title',
	defaultMessage: 'Issues based on <strong>Label</strong>',
	description: 'Label for when labels is selected as the source type of the new board',
});

const labelsDescription = defineMessage({
	id: 'software-board-create.modal.form.source-type-picker.labels-description',
	defaultMessage: 'Show all issues tagged with a specific label',
	description: 'Description for the labels source type option',
});

const allIssuesFromProjectTitle = defineMessage({
	id: 'software-board-create.modal.form.source-type-picker.all-issues-from-project-title',
	defaultMessage: 'All issues from your project',
	description:
		'Text shown in the closed dropdown when the all issues from your project source type option is selected',
});

const allIssuesFromProjectSelectedTitle = defineMessage({
	id: 'software-board-create.modal.form.source-type-picker.all-issues-from-project-selected-title',
	defaultMessage: 'Issues based on <strong>All issues from your project</strong>',
	description:
		'Label for when "All issues in project" is selected as the source type of the new board',
});

const savedFilterTitle = defineMessage({
	id: 'software-board-create.modal.form.source-type-picker.saved-filter-title',
	defaultMessage: 'Advanced',
	description: 'Label for the saved filters source type dropdown option',
});

const savedFilterSelectedTitle = defineMessage({
	id: 'software-board-create.modal.form.source-type-picker.saved-filter-selected-title',
	defaultMessage: 'Issues based on a <strong>Saved filter</strong>',
	description: 'Label for when issue type is selected as the source type of the new board',
});

const savedFilterDescription = defineMessage({
	id: 'software-board-create.modal.form.source-type-picker.saved-filter-description',
	defaultMessage: 'Show issues from a saved JQL filter',
	description: 'Description for the saved filters source type dropdown option',
});

const teamSelectedTitleIssueTerminologyRefreshM4 = defineMessage({
	id: 'software-board-create.modal.form.source-type-picker.team-selected-title-issue-terminology-refresh-m4',
	defaultMessage: 'Work items based on <strong>Team</strong>',
	description: 'Label for when team is selected as the source type of the new board',
});

const teamDescriptionIssueTerminologyRefreshM4 = defineMessage({
	id: 'software-board-create.modal.form.source-type-picker.team-description-issue-terminology-refresh-m4',
	defaultMessage: 'Show all work items assigned to a team',
	description: 'Description for the team source type dropdown option',
});

const issueTypesTitleIssueTerminologyRefreshM4 = defineMessage({
	id: 'software-board-create.modal.form.source-type-picker.issue-types-title-issue-terminology-refresh-m4',
	defaultMessage: 'Work type',
	description: 'Label for the issue types source type option',
});

const issueTypesSelectedTitleIssueTerminologyRefreshM4 = defineMessage({
	id: 'software-board-create.modal.form.source-type-picker.issue-types-selected-title-issue-terminology-refresh-m4',
	defaultMessage: 'Work items based on <strong>work type</strong>',
	description: 'Label for when issue type is selected as the source type of the new board',
});

const issueTypesDescriptionIssueTerminologyRefreshM4 = defineMessage({
	id: 'software-board-create.modal.form.source-type-picker.issue-types-description-issue-terminology-refresh-m4',
	defaultMessage: 'Show all work items associated with a work type',
	description: 'Description for the issue types source type option',
});

const labelsSelectedTitleIssueTerminologyRefreshM4 = defineMessage({
	id: 'software-board-create.modal.form.source-type-picker.labels-selected-title-issue-terminology-refresh-m4',
	defaultMessage: 'Work items based on <strong>label</strong>',
	description: 'Label for when labels is selected as the source type of the new board',
});

const labelsDescriptionIssueTerminologyRefreshM4 = defineMessage({
	id: 'software-board-create.modal.form.source-type-picker.labels-description-issue-terminology-refresh-m4',
	defaultMessage: 'Show all work items tagged with a specific label',
	description: 'Description for the labels source type option',
});

const allIssuesFromProjectTitleIssueTerminologyRefreshM4 = defineMessage({
	id: 'software-board-create.modal.form.source-type-picker.all-issues-from-project-title-issue-terminology-refresh-m4',
	defaultMessage: 'All work items from your project',
	description:
		'Text shown in the closed dropdown when the all issues from your project source type option is selected',
});

const allIssuesFromProjectSelectedTitleIssueTerminologyRefreshM4 = defineMessage({
	id: 'software-board-create.modal.form.source-type-picker.all-issues-from-project-selected-title-issue-terminology-refresh-m4',
	defaultMessage: 'Work items based on <strong>All work items from your project</strong>',
	description:
		'Label for when "All issues in project" is selected as the source type of the new board',
});

const savedFilterSelectedTitleIssueTerminologyRefreshM4 = defineMessage({
	id: 'software-board-create.modal.form.source-type-picker.saved-filter-selected-title-issue-terminology-refresh-m4',
	defaultMessage: 'Work items based on a <strong>Saved filter</strong>',
	description: 'Label for when issue type is selected as the source type of the new board',
});

const savedFilterDescriptionIssueTerminologyRefreshM4 = defineMessage({
	id: 'software-board-create.modal.form.source-type-picker.saved-filter-description-issue-terminology-refresh-m4',
	defaultMessage: 'Show work items from a saved JQL filter',
	description: 'Description for the saved filters source type dropdown option',
});

const allIssuesFromProjectTitleIssueTerminologyRefreshM4Galaxia = defineMessage({
	id: 'software-board-create.modal.form.source-type-picker.all-issues-from-project-title-issue-terminology-refresh-m4-galaxia',
	defaultMessage: 'All work items from your space',
	description:
		'Text shown in the closed dropdown when the all issues from your project source type option is selected',
});

const allIssuesFromProjectSelectedTitleIssueTerminologyRefreshM4Galaxia = defineMessage({
	id: 'software-board-create.modal.form.source-type-picker.all-issues-from-project-selected-title-issue-terminology-refresh-m4-galaxia',
	defaultMessage: 'Work items based on <strong>All work items from your space</strong>',
	description:
		'Label for when "All issues in project" is selected as the source type of the new board',
});

export const getOptionGroups = ({
	formatMessage,
	projectAvatar,
}: {
	formatMessage: IntlShape['formatMessage'];
	projectAvatar?: string;
}): SourceTypeOptionGroup[] => {
	const allIssuesFromProjectSelectedTitleIssueTerminologyRefreshM4Replacement = expVal(
		'project-terminology-refresh',
		'isEnabled',
		false,
	)
		? allIssuesFromProjectSelectedTitleIssueTerminologyRefreshM4Galaxia
		: allIssuesFromProjectSelectedTitleIssueTerminologyRefreshM4;
	const allIssuesFromProjectTitleIssueTerminologyRefreshM4Replacement = expVal(
		'project-terminology-refresh',
		'isEnabled',
		false,
	)
		? allIssuesFromProjectTitleIssueTerminologyRefreshM4Galaxia
		: allIssuesFromProjectTitleIssueTerminologyRefreshM4;
	const allIssuesFromProjectTitleMessage = fg('jira-issue-terminology-update-m4')
		? allIssuesFromProjectTitleIssueTerminologyRefreshM4Replacement
		: allIssuesFromProjectTitle;
	const allIssuesFromProjectSelectedTitleMessage = fg('jira-issue-terminology-update-m4')
		? allIssuesFromProjectSelectedTitleIssueTerminologyRefreshM4Replacement
		: allIssuesFromProjectSelectedTitle;
	const teamSelectedTitleMessage = fg('jira-issue-terminology-update-m4')
		? teamSelectedTitleIssueTerminologyRefreshM4
		: teamSelectedTitle;
	const teamDescriptionMessage = fg('jira-issue-terminology-update-m4')
		? teamDescriptionIssueTerminologyRefreshM4
		: teamDescription;
	const issueTypesTitleMessage = fg('jira-issue-terminology-update-m4')
		? issueTypesTitleIssueTerminologyRefreshM4
		: issueTypesTitle;
	const issueTypesSelectedTitleMessage = fg('jira-issue-terminology-update-m4')
		? issueTypesSelectedTitleIssueTerminologyRefreshM4
		: issueTypesSelectedTitle;
	const issueTypesDescriptionMessage = fg('jira-issue-terminology-update-m4')
		? issueTypesDescriptionIssueTerminologyRefreshM4
		: issueTypesDescription;
	const labelsSelectedTitleMessage = fg('jira-issue-terminology-update-m4')
		? labelsSelectedTitleIssueTerminologyRefreshM4
		: labelsSelectedTitle;
	const labelsDescriptionMessage = fg('jira-issue-terminology-update-m4')
		? labelsDescriptionIssueTerminologyRefreshM4
		: labelsDescription;
	const savedFilterSelectedTitleMessage = fg('jira-issue-terminology-update-m4')
		? savedFilterSelectedTitleIssueTerminologyRefreshM4
		: savedFilterSelectedTitle;
	const savedFilterDescriptionMessage = fg('jira-issue-terminology-update-m4')
		? savedFilterDescriptionIssueTerminologyRefreshM4
		: savedFilterDescription;
	return [
		{
			options: [
				{
					label: formatMessage(allIssuesFromProjectTitleMessage),
					title: formatMessage(allIssuesFromProjectTitleMessage),
					selectedTitle: allIssuesFromProjectSelectedTitleMessage,
					value: null,
					icon: projectAvatar || ProjectIcon,
				},
			],
		},
		{
			options: [
				{
					label: formatMessage(teamTitle),
					title: formatMessage(teamTitle),
					selectedTitle: teamSelectedTitleMessage,
					description: formatMessage(teamDescriptionMessage),
					value: BOARD_SOURCE_FIELDS.TEAM,
					icon: TeamsIcon,
				},
				{
					label: formatMessage(issueTypesTitleMessage),
					title: formatMessage(issueTypesTitleMessage),
					selectedTitle: issueTypesSelectedTitleMessage,
					description: formatMessage(issueTypesDescriptionMessage),
					value: BOARD_SOURCE_FIELDS.ISSUE_TYPES,
					icon: IssuesIcon,
				},
				{
					label: formatMessage(labelsTitle),
					title: formatMessage(labelsTitle),
					selectedTitle: labelsSelectedTitleMessage,
					description: formatMessage(labelsDescriptionMessage),
					value: BOARD_SOURCE_FIELDS.LABELS,
					icon: LabelIcon,
				},
			],
		},
		{
			options: [
				{
					label: formatMessage(savedFilterTitle),
					title: formatMessage(savedFilterTitle),
					selectedTitle: savedFilterSelectedTitleMessage,
					description: formatMessage(savedFilterDescriptionMessage),
					value: BOARD_SOURCE_FIELDS.SAVED_FILTER,
					icon: FilterIcon,
				},
			],
		},
	];
};
